import { t } from '@if/core/i18n'
import { EFinancingProgram, ELanguage, ELoanPurpose } from '@if/core/types'
import { formatNumber } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { PersonalLoanCalculatorConstants } from '@src/types/Constants'

const { MIN_AMOUNT } = PersonalLoanCalculatorConstants

export enum ECommunicationType {
  Email = 'email',
  SMS = 'sms',
  Both = 'both',
}

export const invitationSchema = yup.object({
  firstName: yup.text().max(50).required(),
  lastName: yup.text().max(50).required(),
  languageId: yup
    .mixed<ELanguage>()
    .oneOf(Object.values(ELanguage).filter((value) => typeof value === 'number'))
    .required()
    .default(ELanguage.English),
  communicationType: yup.text().oneOf(Object.values(ECommunicationType)).required().default(ECommunicationType.Both),
  email: yup.text().when('communicationType', {
    is: (val: ECommunicationType) => val === ECommunicationType.Email || val === ECommunicationType.Both,
    then: (schema) => schema.email().max(60).required('Email is required when email communication is selected'),
    otherwise: (schema) => schema.notRequired(),
  }),
  mobilePhoneNumber: yup.text().when('communicationType', {
    is: (val: ECommunicationType) => val === ECommunicationType.SMS || val === ECommunicationType.Both,
    then: (schema) =>
      schema.validCanadianPhone().required('Phone number is required when SMS communication is selected'),
    otherwise: (schema) => schema.notRequired(),
  }),
  confirmationChecked: yup.boolean().oneOf([true], 'You must confirm to proceed').required(),
  financingProgramId: yup
    .text()
    .oneOf(Object.values(EFinancingProgram))
    .required()
    .default(EFinancingProgram.MechanicalWarranty),
  serviceCategory: yup.text().oneOf(Object.values(ELoanPurpose)).optional(),
  // Optional financing amount, Personal program only. The dialog hides the field for other
  // programs, so it stays null there; the bounds only run when a value was entered. The max is the
  // category-based ceiling ($maxLoanValue, Medicard vs regular) the dialog feeds via context.
  amount: yup
    .integer()
    .nullable()
    .min(MIN_AMOUNT, () => t('common.errors.minimumAmount', { min: formatNumber(MIN_AMOUNT) }))
    .max(yup.ref('$maxLoanValue'), ({ max }) => t('common.errors.maximumAmount', { max: formatNumber(max) }))
    .default(null),
  /** Set when the form was refilled from Resend on a history entry, null for a plain send. */
  resentFromInvitationId: yup.text().nullable().optional().default(null),
  isBannerUser: yup.boolean().default(false),
  // Null (not '') when unset: an Autocomplete's empty value must be null or one of its options.
  merchantId: yup
    .text()
    .nullable()
    .default(null)
    .when('isBannerUser', {
      is: true,
      then: (schema) => schema.required('Please select a merchant'),
      otherwise: (schema) => schema.notRequired(),
    }),
})

export type InvitationFormData = yup.InferSubmit<typeof invitationSchema>
